import { Optional, Component, OnInit, OnChanges, OnDestroy, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ViewChild } from '@angular/core';
import { AbstractControl, FormGroup, FormGroupDirective } from '@angular/forms';
import { ControlContainer, ControlValueAccessor } from '@angular/forms';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { MatCalendar } from '@angular/material/datepicker';
import { Subscription } from 'rxjs';
import { languageService } from '@unpispas/upp-base';

/**
 * Modal content for date selection.
 * Allows manual typing and calendar selection.
 */
@Component({
    selector: 'upp-date-modal, upp-date-picker-modal',
    templateUrl: './upp-date-modal.html',
    styleUrls: ['./upp-date-modal.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UppDateModalComponent implements OnInit {
    @Input() value: string | null = null;
    @Input() title = '';
    @Input() placeholder = '';
    @Input() minIso: string | null = null;
    @Input() maxIso: string | null = null;

    dateText = '';
    calendarValue: Date | null = null;
    activeDate: Date | null = null;
    currentMonth = 0;
    currentYear = 0;
    monthOptions: Array<{ value: number; text: string }> = [];
    yearOptions: number[] = [];
    readonly Today = new Date();
    @ViewChild(MatCalendar) calendar: MatCalendar<Date> | null = null;

    constructor(private modalCtrl: ModalController, private change: ChangeDetectorRef, private lang: languageService) {
        // nothing to do
    }

    ngOnInit(): void {
        this.dateText = this.value || '';
        this.calendarValue = this.ToCalendarValue(this.dateText);
        this.activeDate = this.calendarValue ? new Date(this.calendarValue) : new Date(this.Today);
        this.InitializeMonthYearControls();
        this.change.markForCheck();
    }

    OnDateTextChanged(event: Event | CustomEvent): void {
        const customValue = (event as CustomEvent).detail?.value;
        const targetValue = (event.target as HTMLInputElement)?.value;
        const value = (typeof customValue === 'string') ? customValue : (targetValue || '');
        this.dateText = value;

        const date = this.TextToDate(this.dateText);
        this.calendarValue = date ? new Date(date) : null;
        this.activeDate = this.calendarValue ? new Date(this.calendarValue) : this.activeDate;
        this.change.markForCheck();
    }

    OnCalendarChanged(value: Date | null): void {
        const date = value ? new Date(value) : null;
        if (date){
            date.setHours(0, 0, 0, 0);
            this.dateText = this.DateToText(date);
            this.calendarValue = date;
            this.activeDate = new Date(date);
            this.currentMonth = date.getMonth();
            this.currentYear = date.getFullYear();
        }
        this.change.markForCheck();
    }

    OnMonthChanged(value: any): void {
        const month = this.ExtractNumericValue(value);
        if (!Number.isInteger(month) || month < 0 || month > 11){
            return;
        }
        this.currentMonth = month;
        this.SyncCalendarFromMonthYear();
    }

    OnYearChanged(value: any): void {
        const year = this.ExtractNumericValue(value);
        if (!Number.isInteger(year)){
            return;
        }
        this.currentYear = year;
        this.SyncCalendarFromMonthYear();
    }

    OnPrevMonth(): void {
        this.currentMonth = this.currentMonth - 1;
        if (this.currentMonth < 0){
            this.currentMonth = 11;
            this.currentYear = this.currentYear - 1;
        }
        this.SyncCalendarFromMonthYear();
    }

    OnNextMonth(): void {
        this.currentMonth = this.currentMonth + 1;
        if (this.currentMonth > 11){
            this.currentMonth = 0;
            this.currentYear = this.currentYear + 1;
        }
        this.SyncCalendarFromMonthYear();
    }

    get CanApply(): boolean {
        return this.TextToDate(this.dateText) != null;
    }

    async OnClose(): Promise<void> {
        await this.modalCtrl.dismiss(null);
    }

    async OnApply(): Promise<void> {
        const date = this.TextToDate(this.dateText);
        if (!date){
            return;
        }
        await this.modalCtrl.dismiss(this.DateToText(date));
    }

    get MinDate(): Date | null {
        return this.IsoToDate(this.minIso || '');
    }

    get MaxDate(): Date | null {
        return this.IsoToDate(this.maxIso || '');
    }

    private ToCalendarValue(text: string): Date | null {
        const date = this.TextToDate(text);
        return date ? new Date(date) : null;
    }

    private InitializeMonthYearControls(): void {
        const baseDate = this.TextToDate(this.dateText) || new Date();
        baseDate.setHours(0, 0, 0, 0);
        this.currentMonth = baseDate.getMonth();
        this.currentYear = baseDate.getFullYear();

        const minDate = this.IsoToDate(this.minIso || '') || new Date(this.currentYear - 10, 0, 1);
        const maxDate = this.IsoToDate(this.maxIso || '') || new Date(this.currentYear + 10, 11, 31);
        const minYear = minDate.getFullYear();
        const maxYear = maxDate.getFullYear();

        this.yearOptions = [];
        for (let year = minYear; year <= maxYear; year++){
            this.yearOptions.push(year);
        }

        this.monthOptions = [];
        for (let month = 0; month < 12; month++){
            this.monthOptions.push({
                value: month,
                text: this.MonthText(month)
            });
        }
    }

    private SyncCalendarFromMonthYear(): void {
        const currentDate = this.TextToDate(this.dateText) || new Date();
        const targetDay = currentDate.getDate();
        const maxDay = new Date(this.currentYear, this.currentMonth + 1, 0).getDate();
        const nextDay = Math.min(targetDay, maxDay);
        const nextDate = new Date(this.currentYear, this.currentMonth, nextDay);
        nextDate.setHours(0, 0, 0, 0);

        this.dateText = this.DateToText(nextDate);
        this.calendarValue = nextDate;
        this.activeDate = new Date(nextDate);
        if (this.calendar){
            this.calendar.activeDate = new Date(nextDate);
        }
        this.change.markForCheck();
    }

    private ExtractNumericValue(value: any): number {
        if (typeof value === 'number'){
            return value;
        }
        if (typeof value === 'string'){
            return Number(value);
        }
        if (value && typeof value === 'object'){
            const candidates = [
                value.value,
                value.detail?.value,
                value.target?.value
            ];
            for (const candidate of candidates){
                if (typeof candidate === 'number' || typeof candidate === 'string'){
                    return Number(candidate);
                }
            }
        }
        return Number.NaN;
    }

    MonthText(month: number): string {
        const suffix = ('00' + month).slice(-2);
        const fullKey = '@full_month' + suffix;
        const fullValue = this.lang.tr(fullKey);
        if (fullValue && fullValue !== fullKey && fullValue[0] !== '@'){
            return fullValue;
        }

        const shortKey = '@mt' + suffix;
        const shortValue = this.lang.tr(shortKey);
        if (shortValue && shortValue !== shortKey && shortValue[0] !== '@'){
            return shortValue;
        }

        const fallback = new Date(2000, month, 1).toLocaleString('default', { month: 'long' });
        return fallback.charAt(0).toUpperCase() + fallback.slice(1);
    }

    private DateToText(date: Date): string {
        const dd = ('00' + date.getDate()).slice(-2);
        const mm = ('00' + (date.getMonth() + 1)).slice(-2);
        const yy = date.getFullYear();
        return dd + '/' + mm + '/' + yy;
    }

    private TextToDate(value: string): Date | null {
        const parts = (value || '').trim().split('/');
        if (parts.length !== 3){
            return null;
        }

        const dd = Number(parts[0]);
        const mm = Number(parts[1]);
        const yy = Number(parts[2]);
        if (!Number.isInteger(dd) || !Number.isInteger(mm) || !Number.isInteger(yy)){
            return null;
        }

        const date = new Date(yy, mm - 1, dd);
        date.setHours(0, 0, 0, 0);
        if (date.getFullYear() !== yy || date.getMonth() !== (mm - 1) || date.getDate() !== dd){
            return null;
        }

        return date;
    }

    private IsoToDate(value: string): Date | null {
        if (!value){
            return null;
        }

        const parts = value.split('T')[0].split('-');
        if (parts.length !== 3){
            return null;
        }

        const yy = Number(parts[0]);
        const mm = Number(parts[1]);
        const dd = Number(parts[2]);
        if (!Number.isInteger(dd) || !Number.isInteger(mm) || !Number.isInteger(yy)){
            return null;
        }

        const date = new Date(yy, mm - 1, dd);
        date.setHours(0, 0, 0, 0);
        if (date.getFullYear() !== yy || date.getMonth() !== (mm - 1) || date.getDate() !== dd){
            return null;
        }

        return date;
    }
}

/**
 * Date picker component for mobile and desktop.
 * Uses modal dialog with manual date input and calendar.
 */
@Component({
    selector: 'upp-date, upp-date-picker',
    templateUrl: './upp-date.html',
    styleUrls: ['./upp-date.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UppDateComponent),
            multi: true
        }
    ]
})
export class UppDateComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
    @Input() formControlName = '';
    @Input() disabled = false;
    @Input() readonly = false;
    @Input() placeholder = '';
    @Input() title = '';
    @Input() min: string | null = null;
    @Input() max: string | null = null;
    @Input() icon = 'calendar-clear-outline';
    @Output() Changed = new EventEmitter<string | null>();

    public formGroup: FormGroup | null = null;
    public formControl: AbstractControl | null = null;

    private _innerValue: string | null = null;
    private _formSubscription: Subscription | null = null;

    /* eslint-disable @typescript-eslint/no-unused-vars */
    private _onChangeCallback = (v: any) => {
        // nothing to do (required by angular)
    }
    private _onTouchCallback = () => {
        // nothing to do (required by angular)
    }
    /* eslint-enable @typescript-eslint/no-unused-vars */

    constructor(
        private modalCtrl: ModalController,
        private change: ChangeDetectorRef,
        @Optional() private controlContainer: ControlContainer
    ) {
        // nothing to do
    }

    ngOnInit(): void {
        if (this.controlContainer && this.controlContainer instanceof FormGroupDirective){
            this.formGroup = this.controlContainer.form;
        }

        this.formControl = (this.formGroup && this.formControlName) ? this.formGroup.get(this.formControlName) : null;
        if (this.formControl){
            this.value = this.formControl.value || null;
            this._formSubscription = this.formControl.valueChanges.subscribe(() => {
                this.value = this.formControl?.value || null;
            });
        }
    }

    ngOnChanges(): void {
        this.change.markForCheck();
    }

    ngOnDestroy(): void {
        if (this._formSubscription){
            this._formSubscription.unsubscribe();
            this._formSubscription = null;
        }
    }

    get value(): string | null {
        return this._innerValue;
    }

    set value(v: string | null) {
        if (v !== this._innerValue){
            this._innerValue = v;
            this._onChangeCallback(v);
            this._onTouchCallback();
            this.Changed.emit(v);
            this.change.markForCheck();
        }
    }

    get ViewTitle(): string {
        return this.title || this.placeholder;
    }

    get IsDisabled(): boolean {
        if (this.disabled || this.readonly){
            return true;
        }
        if (this.formControl){
            return this.formControl.disabled;
        }
        return false;
    }

    async OnOpenPicker(): Promise<void> {
        if (this.IsDisabled){
            return;
        }

        const modal = await this.modalCtrl.create({
            component: UppDateModalComponent,
            cssClass: 'modal-datetime',
            componentProps: {
                value: this.value,
                title: this.ViewTitle,
                placeholder: this.placeholder,
                minIso: this.min,
                maxIso: this.max
            }
        });

        await modal.present();
        const result = await modal.onDidDismiss<string | null>();
        if (result.data != null){
            this.value = result.data;
        }
    }

    writeValue(value: string | null): void {
        if (value !== this._innerValue){
            this._innerValue = value;
            this.change.markForCheck();
        }
    }

    registerOnChange(fn: any): void {
        this._onChangeCallback = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouchCallback = fn;
    }
}
